import { Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { LogoFull } from "@/components/Logo";
import {
  ClipboardList, BarChart3, Sparkles, Brain, TrendingUp, Target,
  Users, Moon, Award, Shield, CalendarDays, Clock, MessageCircle,
  GitBranch, Dna, Check, X, Mail, Loader2, CheckCircle,
} from "lucide-react";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] },
});

/* ─── CountUp component ─── */
function CountUp({ target, suffix = "", duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    const end = parseInt(target);
    if (isNaN(end)) { setCount(target); return; }
    let start = 0;
    const step = end / (duration * 60);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [inView, target, duration]);

  return <span ref={ref}>{typeof count === "number" ? count : target}{suffix}</span>;
}

const HOW_IT_WORKS = [
  {
    icon: ClipboardList,
    title: "Check in daily",
    desc: "Rate your mood, stress, energy, and sleep in under 60 seconds.",
    color: "#2EAF7D",
  },
  {
    icon: BarChart3,
    title: "Get your score",
    desc: "See your daily wellness score that connects all your health signals.",
    color: "#0B9FBD",
  },
  {
    icon: Sparkles,
    title: "Take action",
    desc: "Get one clear, personalized recommendation based on what works for you.",
    color: "#E8983A",
  },
];

const FEATURES = [
  {
    icon: Brain,
    badge: "AI-POWERED",
    title: "Personal Health Twin",
    desc: "After 30 days, AI generates a living portrait of how your body and mind work. Completely unique to you.",
    color: "#0B9FBD",
  },
  {
    icon: TrendingUp,
    badge: "PROACTIVE",
    title: "Predictive Intelligence",
    desc: "Detects burnout, stress accumulation, and under-recovery before you feel it. Your AI gets smarter daily.",
    color: "#2EAF7D",
  },
  {
    icon: Target,
    badge: "ACTIONABLE",
    title: "7-Day Micro-Challenges",
    desc: "AI proposes personalized experiments. 'Try screen cutoff at 10:30pm.' Track the impact on your score.",
    color: "#E8983A",
  },
  {
    icon: Users,
    badge: "SOCIAL",
    title: "Accountability Circles",
    desc: "Join wellness groups. See who checked in. Nudge friends. Leaderboards that reward consistency, not perfection.",
    color: "#025067",
  },
  {
    icon: Moon,
    badge: "MINDFUL",
    title: "Evening Reflection",
    desc: "A 20-second calming ritual. Rate your day, capture one word. Feeds tomorrow's AI insight.",
    color: "#7B68AE",
  },
  {
    icon: Award,
    badge: "GAMIFIED",
    title: "XP, Levels & Badges",
    desc: "Earn XP for showing up, not for high scores. 10 levels, 20 badges. Effort is what matters.",
    color: "#D4A24A",
  },
];

const MINI_FEATURES = [
  { icon: Shield, label: "Streak Shields" },
  { icon: CalendarDays, label: "Monthly Wrapped" },
  { icon: Clock, label: "Time Capsule" },
  { icon: MessageCircle, label: "AI Coach" },
  { icon: GitBranch, label: "Habit Correlations" },
  { icon: Dna, label: "Score DNA" },
];

const HERO_STATS = [
  { value: "75+", label: "Avg score after 2 weeks", numericValue: 75, suffix: "+" },
  { value: "30s", label: "Daily check-in time", numericValue: 30, suffix: "s" },
  { value: "7", label: "Risk types detected", numericValue: 7, suffix: "" },
  { value: "24/7", label: "AI-powered insights", numericValue: null, suffix: "" },
];

const DIFFERENTIATORS = [
  {
    title: "Prediction, not reaction",
    ours: "Alerts you 2 days before burnout hits",
    theirs: "Tells you that you slept badly last night",
  },
  {
    title: "One score, not 50 charts",
    ours: "A single wellness score connecting mood, sleep, stress, and energy",
    theirs: "Separate dashboards for every metric with no context",
  },
  {
    title: "Action, not just data",
    ours: "One personalized recommendation each day based on what actually works for you",
    theirs: "Generic tips like 'drink more water' repeated on loop",
  },
  {
    title: "Built for consistency",
    ours: "Streak shields, XP for effort, and friends who nudge you back",
    theirs: "Shame-based streaks that reset and guilt you into quitting",
  },
  {
    title: "AI that learns you",
    ours: "Your Health Twin adapts to your patterns and gets smarter every week",
    theirs: "Same algorithm for every user regardless of lifestyle",
  },
  {
    title: "Privacy by design",
    ours: "Your data stays yours. Export anytime, delete everything in one tap",
    theirs: "Data sold to advertisers or locked behind account deletion requests",
  },
];

const API_URL = process.env.REACT_APP_API_URL || 'https://api.livebetter.one';

/* ─── Reusable subscribe hook ─── */
function useSubscribe(source) {
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [result, setResult] = useState(null); // { type: 'success'|'error', message }

  const handleSubscribe = async () => {
    if (!email.trim() || subscribing) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setResult({ type: 'error', message: 'Please enter a valid email.' });
      return;
    }
    setSubscribing(true);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source }),
      });
      const data = await res.json();
      if (res.ok) {
        setResult({ type: 'success', message: data.message || "You're in!" });
        setEmail('');
      } else {
        setResult({ type: 'error', message: data.detail || 'Something went wrong.' });
      }
    } catch {
      setResult({ type: 'error', message: 'Network error. Please try again.' });
    } finally {
      setSubscribing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubscribe();
  };

  return { email, setEmail, subscribing, result, handleSubscribe, handleKeyDown };
}

export default function LandingPage() {
  const scrollToDownload = () => {
    document.getElementById("download")?.scrollIntoView({ behavior: "smooth" });
  };
  const scrollToFeatures = () => {
    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
  };

  // Newsletter subscription state
  const newsletter = useSubscribe('landing_page');
  const downloadWaitlist = useSubscribe('download_cta');

  // 3D tilt state for hero score card
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x: y * -8, y: x * 8 });
  };

  const handleMouseLeave = () => setTilt({ x: 0, y: 0 });

  // Parallax scroll for hero orbs
  const { scrollY } = useScroll();
  const orbY1 = useTransform(scrollY, [0, 500], [0, -60]);
  const orbY2 = useTransform(scrollY, [0, 500], [0, -30]);

  return (
    <div style={{ background: "#F8F9FA", minHeight: "100vh", overflowX: "hidden" }}>

      {/* Keyframe animations */}
      <style>{`
        @keyframes shimmerText {
          0%, 100% { background-position: -200% 0; }
          50% { background-position: 200% 0; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* ─── Sticky Nav ─── */}
      <nav
        style={{
          position: "sticky", top: 0, zIndex: 40,
          background: "rgba(248,249,250,0.88)", backdropFilter: "blur(16px)",
          borderBottom: "1px solid rgba(0,0,0,0.04)",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          maxWidth: 1120, margin: "0 auto", padding: "14px 24px",
        }}>
          <LogoFull iconSize={26} />
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <Link to="/blog" style={{ color: "#5A7580", fontSize: 14, fontWeight: 600, textDecoration: "none" }}>Blog</Link>
            <button
              onClick={scrollToDownload}
              style={{
                background: "#0B9FBD", color: "#fff", border: "none", cursor: "pointer",
                fontSize: 14, fontWeight: 600, padding: "10px 24px", borderRadius: 999,
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(11,159,189,0.3)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section style={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        textAlign: "center", padding: "80px 24px 60px", position: "relative",
      }}>
        {/* Ambient gradient orbs with parallax */}
        <motion.div style={{
          position: "absolute", top: "10%", left: "20%", width: 400, height: 400,
          borderRadius: "50%", background: "#0B9FBD", opacity: 0.04,
          filter: "blur(100px)", pointerEvents: "none",
          y: orbY1,
        }} />
        <motion.div style={{
          position: "absolute", bottom: "15%", right: "20%", width: 300, height: 300,
          borderRadius: "50%", background: "#025067", opacity: 0.03,
          filter: "blur(80px)", pointerEvents: "none",
          y: orbY2,
        }} />

        {/* Badge */}
        <motion.div {...fadeUp(0)} style={{ position: "relative", zIndex: 1 }}>
          <span style={{
            display: "inline-block", padding: "6px 16px", borderRadius: 999,
            background: "rgba(11,159,189,0.08)", color: "#0B9FBD",
            fontSize: 12, fontWeight: 700, letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}>
            Your Daily Wellness Companion
          </span>
        </motion.div>

        <motion.h1
          {...fadeUp(0.08)}
          style={{
            fontFamily: "Outfit, sans-serif", fontSize: "clamp(36px, 6vw, 64px)",
            fontWeight: 700, color: "#021E28", lineHeight: 1.1,
            marginTop: 24, letterSpacing: "-0.02em", maxWidth: 720,
          }}
        >
          One score. One insight.{" "}
          <span style={{ color: "#0B9FBD" }}>One action to feel better.</span>
        </motion.h1>

        <motion.p
          {...fadeUp(0.14)}
          style={{
            fontFamily: "Manrope, sans-serif", fontSize: "clamp(16px, 2vw, 19px)",
            color: "#5A7580", marginTop: 20, maxWidth: 540, lineHeight: 1.6,
          }}
        >
          Check in with how you feel. Get a wellness score that connects your sleep,
          stress, energy, and mood. Then receive one clear action to improve your day.
        </motion.p>

        {/* CTA buttons */}
        <motion.div {...fadeUp(0.2)} style={{ display: "flex", gap: 14, marginTop: 36, flexWrap: "wrap", justifyContent: "center" }}>
          <button
            onClick={scrollToDownload}
            style={{
              background: "linear-gradient(135deg, #025067, #0B9FBD)",
              color: "#fff", border: "none", cursor: "pointer",
              fontSize: 16, fontWeight: 600, padding: "14px 36px", borderRadius: 999,
              boxShadow: "0 4px 24px rgba(11,159,189,0.3)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 32px rgba(11,159,189,0.4)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 24px rgba(11,159,189,0.3)"; }}
          >
            Start Free
          </button>
          <button
            onClick={scrollToFeatures}
            style={{
              background: "transparent", color: "#025067",
              border: "2px solid rgba(2,80,103,0.2)", cursor: "pointer",
              fontSize: 16, fontWeight: 600, padding: "14px 36px", borderRadius: 999,
              transition: "transform 0.2s, border-color 0.2s",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = "rgba(2,80,103,0.4)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.borderColor = "rgba(2,80,103,0.2)"; }}
          >
            See Features
          </button>
        </motion.div>

        {/* Mock Score Card with 3D tilt + floating animation */}
        <motion.div
          ref={cardRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          animate={{
            rotateX: tilt.x,
            rotateY: tilt.y,
            y: [0, -6, 0],
          }}
          transition={{
            rotateX: { type: "spring", stiffness: 300, damping: 30 },
            rotateY: { type: "spring", stiffness: 300, damping: 30 },
            y: { duration: 4, repeat: Infinity, ease: "easeInOut" },
          }}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          style={{
            marginTop: 48, background: "#fff", borderRadius: 24,
            padding: "32px 40px", boxShadow: "0 8px 40px rgba(0,0,0,0.06)",
            border: "1px solid rgba(0,0,0,0.04)", maxWidth: 360,
            position: "relative", zIndex: 1,
            perspective: 800, transformStyle: "preserve-3d",
          }}
        >
          <p style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.1em",
            color: "#0B9FBD", textTransform: "uppercase", marginBottom: 12,
          }}>
            Daily Wellness Score
          </p>

          {/* Score ring using SVG for animation */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            style={{ position: "relative", width: 120, height: 120, margin: "0 auto 16px" }}
          >
            <svg width="120" height="120" viewBox="0 0 120 120" style={{ display: "block" }}>
              {/* Background ring */}
              <circle cx="60" cy="60" r="52" fill="none" stroke="#E8ECF0" strokeWidth="8" />
              {/* Animated score ring */}
              <motion.circle
                cx="60" cy="60" r="52"
                fill="none"
                stroke="#2EAF7D"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 52}
                initial={{ strokeDashoffset: 2 * Math.PI * 52 }}
                whileInView={{ strokeDashoffset: 2 * Math.PI * 52 * (1 - 0.75) }}
                viewport={{ once: true }}
                transition={{ duration: 1.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                style={{ transform: "rotate(-90deg)", transformOrigin: "50% 50%" }}
              />
            </svg>
            {/* Score number */}
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
              <motion.span
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.8 }}
                style={{ fontFamily: "Outfit, sans-serif", fontSize: 36, fontWeight: 700, color: "#021E28" }}
              >
                75
              </motion.span>
            </div>
          </motion.div>

          {/* Insight quote */}
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 1.2, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{ fontSize: 13, color: "#5A7580", lineHeight: 1.5, marginBottom: 16 }}
          >
            "Your energy peaks mid-week. Try a Tuesday workout."
          </motion.p>

          {/* Metric pills */}
          <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
            {[
              { label: "Sleep", value: "7.2h", color: "#7B68AE" },
              { label: "Mood", value: "8", color: "#2EAF7D" },
              { label: "Energy", value: "6", color: "#E8983A" },
            ].map((metric, i) => (
              <motion.div
                key={metric.label}
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 1.4 + i * 0.15, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: 999,
                  background: `${metric.color}0D`,
                  fontSize: 12, fontWeight: 600,
                }}
              >
                <span style={{ color: metric.color }}>{metric.label}</span>
                <span style={{ color: "#021E28" }}>{metric.value}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Stats Row with CountUp */}
        <motion.div
          {...fadeUp(0.34)}
          style={{
            display: "flex", flexWrap: "wrap", gap: 32,
            justifyContent: "center", marginTop: 48, position: "relative", zIndex: 1,
          }}
        >
          {HERO_STATS.map((stat, i) => (
            <div key={i} style={{ textAlign: "center", minWidth: 120 }}>
              <p style={{
                fontFamily: "Outfit, sans-serif", fontSize: 28,
                fontWeight: 700, color: "#021E28",
              }}>
                {stat.numericValue !== null ? (
                  <CountUp target={stat.numericValue} suffix={stat.suffix} />
                ) : (
                  stat.value
                )}
              </p>
              <p style={{ fontSize: 13, color: "#5A7580", marginTop: 4 }}>
                {stat.label}
              </p>
            </div>
          ))}
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", bottom: 40, cursor: "pointer", zIndex: 1 }}
          onClick={scrollToFeatures}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#5A7580" strokeWidth="2">
            <path d="M12 5v14M5 12l7 7 7-7" />
          </svg>
        </motion.div>
      </section>

      {/* ─── How It Works ─── */}
      <section style={{ padding: "80px 24px", maxWidth: 1120, margin: "0 auto" }}>
        <motion.div {...fadeUp()} style={{ textAlign: "center", marginBottom: 56 }}>
          <h2 style={{
            fontFamily: "Outfit, sans-serif", fontSize: "clamp(28px, 4vw, 40px)",
            fontWeight: 700, color: "#021E28",
          }}>
            How it works
          </h2>
          <p style={{ color: "#5A7580", marginTop: 12, fontSize: 16 }}>
            Three steps. Under two minutes. Real results.
          </p>
        </motion.div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 24,
          position: "relative",
        }}>
          {/* Animated connecting line (desktop only) */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            style={{
              height: 2,
              background: "linear-gradient(90deg, #2EAF7D, #0B9FBD, #E8983A)",
              transformOrigin: "left",
              position: "absolute",
              top: 60, left: "18%", right: "18%",
              zIndex: 0,
              display: "var(--connector-display, none)",
            }}
          />
          <style>{`
            @media (min-width: 900px) {
              :root { --connector-display: block !important; }
            }
          `}</style>

          {HOW_IT_WORKS.map((step, i) => (
            <motion.div
              key={i}
              {...fadeUp(i * 0.1)}
              style={{
                background: "#fff", borderRadius: 20, padding: 36,
                border: "1px solid rgba(0,0,0,0.04)",
                boxShadow: "0 1px 4px rgba(0,0,0,0.02)",
                textAlign: "center",
                transition: "transform 0.25s, box-shadow 0.25s",
                position: "relative", zIndex: 1,
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.06)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.02)"; }}
            >
              {/* Step number */}
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ type: "spring", stiffness: 400, damping: 15, delay: 0.2 + i * 0.1 }}
                style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: `${step.color}15`, color: step.color,
                  fontSize: 13, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  position: "absolute", top: 16, right: 16,
                }}
              >
                {i + 1}
              </motion.div>

              <div style={{
                width: 56, height: 56, borderRadius: 16,
                background: `${step.color}12`, display: "flex",
                alignItems: "center", justifyContent: "center", margin: "0 auto 20px",
              }}>
                <step.icon style={{ width: 24, height: 24, color: step.color }} />
              </div>
              <h3 style={{
                fontFamily: "Outfit, sans-serif", fontSize: 20,
                fontWeight: 700, color: "#021E28", marginBottom: 8,
              }}>
                {step.title}
              </h3>
              <p style={{ color: "#5A7580", fontSize: 15, lineHeight: 1.6 }}>
                {step.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── Features Grid ─── */}
      <section id="features" style={{ padding: "80px 24px", maxWidth: 1120, margin: "0 auto" }}>
        <motion.div {...fadeUp()} style={{ textAlign: "center", marginBottom: 56 }}>
          <span style={{
            display: "inline-block", padding: "6px 16px", borderRadius: 999,
            background: "rgba(46,175,125,0.08)", color: "#2EAF7D",
            fontSize: 12, fontWeight: 700, letterSpacing: "0.08em",
            textTransform: "uppercase", marginBottom: 16,
          }}>
            24 Features Built
          </span>
          <h2 style={{
            fontFamily: "Outfit, sans-serif", fontSize: "clamp(28px, 4vw, 40px)",
            fontWeight: 700, color: "#021E28",
          }}>
            Everything you need to feel your best
          </h2>
          <p style={{ color: "#5A7580", marginTop: 12, fontSize: 16, maxWidth: 520, margin: "12px auto 0" }}>
            Not just tracking. Intelligence that learns you, predicts for you, and grows with you.
          </p>
        </motion.div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 24,
        }}>
          {FEATURES.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{
                y: -8,
                rotateX: 2,
                rotateY: -2,
                boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                transition: { duration: 0.3 },
              }}
              style={{
                background: "#fff", borderRadius: 20, padding: 32,
                border: "1px solid rgba(0,0,0,0.04)",
                boxShadow: "0 1px 4px rgba(0,0,0,0.02)",
                perspective: 600, transformStyle: "preserve-3d",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <motion.div
                  whileHover={{ scale: 1.15 }}
                  transition={{ type: "spring", stiffness: 400, damping: 20 }}
                  style={{
                    width: 48, height: 48, borderRadius: 14,
                    background: `${f.color}12`, display: "flex",
                    alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}
                >
                  <f.icon style={{ width: 22, height: 22, color: f.color }} />
                </motion.div>
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
                  color: f.color, textTransform: "uppercase",
                  padding: "3px 8px", borderRadius: 6,
                  background: `${f.color}0A`,
                }}>
                  {f.badge}
                </span>
              </div>
              <h3 style={{
                fontFamily: "Outfit, sans-serif", fontSize: 18,
                fontWeight: 700, color: "#021E28", marginBottom: 8,
              }}>
                {f.title}
              </h3>
              <p style={{ color: "#5A7580", fontSize: 15, lineHeight: 1.6 }}>
                {f.desc}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Mini feature pills with bounce-in */}
        <div
          style={{
            display: "flex", flexWrap: "wrap", gap: 12,
            justifyContent: "center", marginTop: 40,
          }}
        >
          {MINI_FEATURES.map((mf, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", stiffness: 300, damping: 15, delay: i * 0.06 }}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "8px 16px", borderRadius: 999,
                background: "#fff", border: "1px solid rgba(0,0,0,0.06)",
                fontSize: 13, fontWeight: 600, color: "#5A7580",
              }}
            >
              <mf.icon style={{ width: 14, height: 14, color: "#0B9FBD" }} />
              {mf.label}
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── Why LiveBetter Is Different ─── */}
      <section style={{ padding: "80px 24px", maxWidth: 1120, margin: "0 auto" }}>
        <motion.div {...fadeUp()} style={{ textAlign: "center", marginBottom: 56 }}>
          <h2 style={{
            fontFamily: "Outfit, sans-serif", fontSize: "clamp(28px, 4vw, 40px)",
            fontWeight: 700, color: "#021E28",
          }}>
            Why LiveBetter is different
          </h2>
          <p style={{ color: "#5A7580", marginTop: 12, fontSize: 16, maxWidth: 460, margin: "12px auto 0" }}>
            Most health apps react. We predict. Most track. We act.
          </p>
        </motion.div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 24,
        }}>
          {DIFFERENTIATORS.map((d, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.65, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              style={{
                background: "#fff", borderRadius: 20, padding: 32,
                border: "1px solid rgba(0,0,0,0.04)",
                boxShadow: "0 1px 4px rgba(0,0,0,0.02)",
              }}
            >
              <h3 style={{
                fontFamily: "Outfit, sans-serif", fontSize: 18,
                fontWeight: 700, color: "#021E28", marginBottom: 20,
              }}>
                {d.title}
              </h3>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
                <div style={{
                  width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                  background: "rgba(46,175,125,0.1)", display: "flex",
                  alignItems: "center", justifyContent: "center", marginTop: 1,
                }}>
                  <Check style={{ width: 13, height: 13, color: "#2EAF7D" }} />
                </div>
                <p style={{ fontSize: 14, color: "#021E28", lineHeight: 1.5 }}>
                  {d.ours}
                </p>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <div style={{
                  width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                  background: "rgba(0,0,0,0.04)", display: "flex",
                  alignItems: "center", justifyContent: "center", marginTop: 1,
                }}>
                  <X style={{ width: 13, height: 13, color: "#8B95A5" }} />
                </div>
                <p style={{ fontSize: 14, color: "#8B95A5", lineHeight: 1.5 }}>
                  {d.theirs}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── Newsletter / Weekly Health Insights ─── */}
      <section style={{ padding: "80px 24px", maxWidth: 1120, margin: "0 auto" }}>
        <motion.div
          {...fadeUp()}
          style={{
            background: "#fff", borderRadius: 28,
            padding: "64px 32px", textAlign: "center",
            border: "1px solid rgba(0,0,0,0.04)",
            boxShadow: "0 1px 4px rgba(0,0,0,0.02)",
          }}
        >
          <motion.div
            {...fadeUp(0.05)}
            style={{
              width: 56, height: 56, borderRadius: 16,
              background: "rgba(11,159,189,0.08)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <Mail style={{ width: 24, height: 24, color: "#0B9FBD" }} />
          </motion.div>

          <motion.h2
            {...fadeUp(0.08)}
            style={{
              fontFamily: "Outfit, sans-serif",
              fontSize: "clamp(24px, 3.5vw, 36px)",
              fontWeight: 700, color: "#021E28", marginBottom: 12,
            }}
          >
            Get weekly health insights
          </motion.h2>
          <motion.p
            {...fadeUp(0.12)}
            style={{
              color: "#5A7580", fontSize: 16, maxWidth: 460,
              margin: "0 auto 32px", lineHeight: 1.6,
            }}
          >
            Science-backed tips, product updates, and wellness strategies
            delivered to your inbox every week. No spam, unsubscribe anytime.
          </motion.p>

          {newsletter.result?.type === 'success' ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: 10, padding: "16px 24px", borderRadius: 999,
                background: "rgba(46,175,125,0.08)", maxWidth: 360,
                margin: "0 auto",
              }}
            >
              <CheckCircle style={{ width: 20, height: 20, color: "#2EAF7D" }} />
              <span style={{ fontSize: 15, fontWeight: 600, color: "#2EAF7D" }}>
                {newsletter.result.message}
              </span>
            </motion.div>
          ) : (
            <motion.div {...fadeUp(0.16)}>
              <div style={{
                display: "flex", gap: 10, maxWidth: 440, margin: "0 auto",
                flexWrap: "wrap", justifyContent: "center",
              }}>
                <input
                  type="email"
                  value={newsletter.email}
                  onChange={(e) => newsletter.setEmail(e.target.value)}
                  onKeyDown={newsletter.handleKeyDown}
                  placeholder="your@email.com"
                  style={{
                    flex: "1 1 240px", padding: "14px 20px",
                    borderRadius: 999, border: "1.5px solid rgba(0,0,0,0.1)",
                    fontSize: 15, outline: "none",
                    transition: "border-color 0.2s",
                    background: "#F8F9FA",
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = "#0B9FBD"; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(0,0,0,0.1)"; }}
                />
                <button
                  onClick={newsletter.handleSubscribe}
                  disabled={newsletter.subscribing}
                  style={{
                    background: "linear-gradient(135deg, #025067, #0B9FBD)",
                    color: "#fff", border: "none", cursor: newsletter.subscribing ? "default" : "pointer",
                    fontSize: 15, fontWeight: 600, padding: "14px 28px", borderRadius: 999,
                    boxShadow: "0 4px 16px rgba(11,159,189,0.25)",
                    transition: "transform 0.2s, box-shadow 0.2s, opacity 0.2s",
                    opacity: newsletter.subscribing ? 0.7 : 1,
                    display: "flex", alignItems: "center", gap: 8,
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => { if (!newsletter.subscribing) { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(11,159,189,0.35)"; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 16px rgba(11,159,189,0.25)"; }}
                >
                  {newsletter.subscribing ? (
                    <>
                      <Loader2 style={{ width: 16, height: 16, animation: "spin 1s linear infinite" }} />
                      Subscribing...
                    </>
                  ) : (
                    "Subscribe"
                  )}
                </button>
              </div>
              {newsletter.result?.type === 'error' && (
                <motion.p
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    color: "#DC3545", fontSize: 13, fontWeight: 500,
                    marginTop: 10, textAlign: "center",
                  }}
                >
                  {newsletter.result.message}
                </motion.p>
              )}
            </motion.div>
          )}
        </motion.div>
      </section>

      {/* ─── Download CTA ─── */}
      <section
        id="download"
        style={{
          padding: "24px",
          maxWidth: 1120,
          margin: "0 auto 80px",
        }}
      >
        <motion.div
          {...fadeUp()}
          style={{
            background: "#025067", borderRadius: 28,
            padding: "80px 32px", textAlign: "center",
            position: "relative", overflow: "hidden",
          }}
        >
          {/* Subtle gradient overlay */}
          <div style={{
            position: "absolute", inset: 0, opacity: 0.15,
            background: "radial-gradient(circle at 30% 30%, #0B9FBD, transparent 60%), radial-gradient(circle at 70% 70%, #025067, transparent 60%)",
            pointerEvents: "none",
          }} />

          <div style={{ position: "relative", zIndex: 1 }}>
            <motion.h2
              {...fadeUp(0.05)}
              style={{
                fontFamily: "Outfit, sans-serif",
                fontSize: "clamp(28px, 4vw, 44px)",
                fontWeight: 700, marginBottom: 12,
                background: "linear-gradient(90deg, #fff 0%, rgba(255,255,255,0.6) 50%, #fff 100%)",
                backgroundSize: "200% 100%",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                animation: "shimmerText 3s ease-in-out infinite",
              }}
            >
              Ready to feel better?
            </motion.h2>
            <motion.p
              {...fadeUp(0.1)}
              style={{
                color: "rgba(255,255,255,0.6)", fontSize: 17,
                maxWidth: 440, margin: "0 auto 40px", lineHeight: 1.5,
              }}
            >
              Download LiveBetter and start your journey.
            </motion.p>

            <motion.div
              {...fadeUp(0.15)}
              style={{
                display: "flex", gap: 16,
                justifyContent: "center", flexWrap: "wrap",
              }}
            >
              {/* Google Play */}
              <div style={{ textAlign: "center" }}>
                <button
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    background: "rgba(255,255,255,0.1)", color: "#fff",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: 14, padding: "14px 28px", cursor: "default",
                    fontSize: 15, fontWeight: 600, minWidth: 180,
                    justifyContent: "center",
                  }}
                >
                  <svg width="20" height="22" viewBox="0 0 20 22" fill="none">
                    <path d="M1.23 0.344C0.962 0.629 0.8 1.06 0.8 1.614V20.386C0.8 20.94 0.962 21.371 1.23 21.656L1.3 21.724L11.55 11.474V11.286V11.098L1.3 0.848L1.23 0.344Z" fill="currentColor" fillOpacity="0.9"/>
                    <path d="M14.95 14.886L11.55 11.486V11.286V11.086L14.952 7.686L15.04 7.736L19.04 10.036C20.18 10.686 20.18 11.736 19.04 12.386L15.04 14.836L14.95 14.886Z" fill="currentColor" fillOpacity="0.9"/>
                    <path d="M15.04 14.836L11.55 11.286L1.23 21.656C1.63 22.08 2.28 22.13 3.01 21.71L15.04 14.836Z" fill="currentColor" fillOpacity="0.7"/>
                    <path d="M15.04 7.736L3.01 0.862C2.28 0.442 1.63 0.492 1.23 0.916L11.55 11.286L15.04 7.736Z" fill="currentColor" fillOpacity="0.7"/>
                  </svg>
                  Google Play
                </button>
                <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, marginTop: 8, fontWeight: 500 }}>
                  Coming Soon
                </p>
              </div>

              {/* App Store */}
              <div style={{ textAlign: "center" }}>
                <button
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    background: "rgba(255,255,255,0.1)", color: "#fff",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: 14, padding: "14px 28px", cursor: "default",
                    fontSize: 15, fontWeight: 600, minWidth: 180,
                    justifyContent: "center",
                  }}
                >
                  <svg width="18" height="22" viewBox="0 0 18 22" fill="currentColor" fillOpacity="0.9">
                    <path d="M14.94 11.606c-.024-2.478 2.024-3.666 2.116-3.724-1.152-1.684-2.944-1.914-3.584-1.94-1.524-.154-2.976.898-3.75.898-.774 0-1.972-.874-3.24-.852-1.668.024-3.204.97-4.062 2.464-1.732 3.002-.444 7.452 1.244 9.888.824 1.192 1.808 2.532 3.1 2.484 1.244-.05 1.714-.806 3.218-.806 1.504 0 1.926.806 3.24.782 1.338-.024 2.19-1.214 3.008-2.41.948-1.382 1.338-2.72 1.362-2.79-.03-.014-2.614-1.002-2.64-3.976l-.012-.018zM12.44 4.126c.684-.83 1.146-1.982.02-2.094-.95.038-2.1.634-2.782 1.432-.612.71-1.148 1.842-1.004 2.928 1.06.082 2.142-.538 2.766-1.266h1z"/>
                  </svg>
                  App Store
                </button>
                <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 12, marginTop: 8, fontWeight: 500 }}>
                  Coming Soon
                </p>
              </div>
            </motion.div>

            {/* Waitlist email signup */}
            <motion.div {...fadeUp(0.25)} style={{ marginTop: 40 }}>
              <p style={{
                color: "rgba(255,255,255,0.5)", fontSize: 13, fontWeight: 600,
                letterSpacing: "0.04em", marginBottom: 14,
              }}>
                Get notified when we launch
              </p>

              {downloadWaitlist.result?.type === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    gap: 8, padding: "12px 20px", borderRadius: 999,
                    background: "rgba(46,175,125,0.15)",
                  }}
                >
                  <CheckCircle style={{ width: 16, height: 16, color: "#2EAF7D" }} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: "#2EAF7D" }}>
                    {downloadWaitlist.result.message}
                  </span>
                </motion.div>
              ) : (
                <>
                  <div style={{
                    display: "flex", gap: 8, maxWidth: 380, margin: "0 auto",
                    justifyContent: "center",
                  }}>
                    <input
                      type="email"
                      value={downloadWaitlist.email}
                      onChange={(e) => downloadWaitlist.setEmail(e.target.value)}
                      onKeyDown={downloadWaitlist.handleKeyDown}
                      placeholder="your@email.com"
                      style={{
                        flex: "1 1 200px", padding: "11px 16px",
                        borderRadius: 999, border: "1px solid rgba(255,255,255,0.2)",
                        fontSize: 14, outline: "none",
                        background: "rgba(255,255,255,0.08)", color: "#fff",
                        transition: "border-color 0.2s",
                      }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.5)"; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; }}
                    />
                    <button
                      onClick={downloadWaitlist.handleSubscribe}
                      disabled={downloadWaitlist.subscribing}
                      style={{
                        background: "rgba(255,255,255,0.15)",
                        color: "#fff", border: "1px solid rgba(255,255,255,0.25)",
                        cursor: downloadWaitlist.subscribing ? "default" : "pointer",
                        fontSize: 13, fontWeight: 600, padding: "11px 20px", borderRadius: 999,
                        transition: "background 0.2s, opacity 0.2s",
                        opacity: downloadWaitlist.subscribing ? 0.7 : 1,
                        display: "flex", alignItems: "center", gap: 6,
                        whiteSpace: "nowrap",
                      }}
                      onMouseEnter={(e) => { if (!downloadWaitlist.subscribing) e.currentTarget.style.background = "rgba(255,255,255,0.25)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; }}
                    >
                      {downloadWaitlist.subscribing ? (
                        <>
                          <Loader2 style={{ width: 14, height: 14, animation: "spin 1s linear infinite" }} />
                          Joining...
                        </>
                      ) : (
                        "Notify Me"
                      )}
                    </button>
                  </div>
                  {downloadWaitlist.result?.type === 'error' && (
                    <motion.p
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      style={{
                        color: "#FF6B6B", fontSize: 12, fontWeight: 500,
                        marginTop: 8, textAlign: "center",
                      }}
                    >
                      {downloadWaitlist.result.message}
                    </motion.p>
                  )}
                </>
              )}
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ─── Footer ─── */}
      <footer style={{
        padding: "40px 24px 48px", maxWidth: 1120, margin: "0 auto",
        borderTop: "1px solid rgba(0,0,0,0.06)",
      }}>
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", gap: 24,
        }}>
          <LogoFull iconSize={24} />

          <div style={{ display: "flex", gap: 24 }}>
            <Link to="/blog" style={{ color: "#5A7580", fontSize: 14, fontWeight: 500, textDecoration: "none" }}>
              Blog
            </Link>
            <Link to="/privacy" style={{ color: "#5A7580", fontSize: 14, fontWeight: 500, textDecoration: "none" }}>
              Privacy
            </Link>
            <Link to="/terms" style={{ color: "#5A7580", fontSize: 14, fontWeight: 500, textDecoration: "none" }}>
              Terms
            </Link>
          </div>

          <p style={{ color: "#8B95A5", fontSize: 12, textAlign: "center", maxWidth: 480, lineHeight: 1.6 }}>
            LiveBetter is a wellness companion, not a medical device.
            Not intended to diagnose, treat, or prevent any disease.
          </p>

          <p style={{ color: "#8B95A5", fontSize: 11 }}>
            &copy; {new Date().getFullYear()} LiveBetter. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
